import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, formatDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CornerDownRight, Check, X } from "lucide-react";

const STATUS_LABELS = {
  open: { label: "Otevřená", className: "bg-rose-100 text-rose-800 border border-rose-200" },
  replied: { label: "Odpovězeno", className: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  closed: { label: "Uzavřená", className: "bg-slate-200 text-slate-700 border border-slate-300" },
};

const MessageCard = ({ msg, onChange }) => {
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const status = STATUS_LABELS[msg.status] || STATUS_LABELS.open;

  const sendReply = async () => {
    if (reply.trim().length < 1) {
      toast.error("Napište odpověď");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/contact-messages/${msg.id}/reply`, { text: reply });
      toast.success("Odpověď uložena");
      setReply("");
      await onChange();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSaving(false); }
  };

  const close = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/contact-messages/${msg.id}/close`);
      toast.success("Zpráva uzavřena");
      await onChange();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid={`admin-message-${msg.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{msg.name}</span>
            <a href={`mailto:${msg.email}`} className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> {msg.email}
            </a>
            <Badge className={status.className}>{status.label}</Badge>
          </div>
          {msg.subject && <div className="text-sm text-slate-700 mt-1 font-medium">{msg.subject}</div>}
          <div className="text-xs text-slate-400 mt-0.5">{formatDate(msg.created_at)}</div>
        </div>
        {msg.status !== "closed" && (
          <Button size="sm" variant="outline" onClick={close} disabled={saving} data-testid={`admin-message-close-${msg.id}`}>
            <X className="w-3.5 h-3.5 mr-1" /> Uzavřít
          </Button>
        )}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid={`admin-message-body-${msg.id}`}>
        {msg.message}
      </div>

      {(msg.replies || []).length > 0 && (
        <div className="mt-3 space-y-2">
          {msg.replies.map((r, i) => (
            <div key={`${r.at}-${i}`} className="ml-6 bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <CornerDownRight className="w-3.5 h-3.5 text-sky-600" />
                <strong className="text-slate-700">{r.by_admin_name}</strong>
                <span>{formatDate(r.at)}</span>
              </div>
              <div className="text-slate-800 whitespace-pre-wrap">{r.text}</div>
            </div>
          ))}
        </div>
      )}

      {msg.status !== "closed" && (
        <div className="mt-4 space-y-2">
          <Textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={`Odpovědět ${msg.name}…`}
            data-testid={`admin-message-reply-input-${msg.id}`}
          />
          <div className="flex justify-end">
            <Button onClick={sendReply} disabled={saving || !reply.trim()} data-testid={`admin-message-reply-btn-${msg.id}`}>
              <Check className="w-4 h-4 mr-1.5" /> Odeslat odpověď
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export const ContactMessagesPanel = ({ messages, onChange }) => {
  const [filter, setFilter] = useState("all"); // all | open | replied | closed

  const filtered = messages.filter((m) => filter === "all" || m.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap" data-testid="admin-messages-filters">
        {["all", "open", "replied", "closed"].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
            data-testid={`admin-messages-filter-${f}`}
          >
            {f === "all" ? "Vše" : STATUS_LABELS[f].label}
            <span className="ml-1.5 text-xs opacity-70">
              ({f === "all" ? messages.length : messages.filter((m) => m.status === f).length})
            </span>
          </Button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="admin-messages-empty">
          Žádné zprávy v této kategorii.
        </Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map((m) => (
            <MessageCard key={m.id} msg={m} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
};
